import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { 
  Shield, 
  ShieldCheck,
  AlertTriangle, 
  Users, 
  Activity, 
  Plus,
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  HardDrive,
  Zap,
  UserCheck,
  Mail,
  Settings
} from 'lucide-react';
import api, { endpoints } from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';
import SSOConfiguration from '../components/SSOConfiguration';
import { useAuth } from '../contexts/AuthContext';

const Admin = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('health');
  const [loading, setLoading] = useState(true);
  const [systemHealth, setSystemHealth] = useState(null);
  const [users, setUsers] = useState([]);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showNuclearConfirm, setShowNuclearConfirm] = useState(false);
  const [teamManagementLoading, setTeamManagementLoading] = useState(false);
  
  // Enhanced team management state
  const [allTeams, setAllTeams] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [debugOutput, setDebugOutput] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [cursorDebug, setCursorDebug] = useState(null);
  const [cursorDebugLoading, setCursorDebugLoading] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [teamSearch, setTeamSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [newTeam, setNewTeam] = useState({
    name: '',
    description: ''
  });
  
  // Upload debug state removed - CSV upload functionality deprecated
  
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    role: 'user',
    name: ''
  });

  // Maintenance mode state
  const [maintenanceConfig, setMaintenanceConfig] = useState({
    isMaintenanceMode: false,
    message: '',
    enabledAt: null,
    enabledBy: null,
    enabledByUsername: null,
    disabledAt: null,
    disabledBy: null,
    disabledByUsername: null
  });
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);
  const [editingMessage, setEditingMessage] = useState(false);
  const [tempMessage, setTempMessage] = useState('');
  const [nuclearPassword, setNuclearPassword] = useState('');

  // SendGrid state
  const [sendgridConfig, setSendgridConfig] = useState({
    configured: false,
    fromEmail: '',
    fromName: '',
    replyTo: ''
  });
  const [sendgridLoading, setSendgridLoading] = useState(false);
  const [testEmailData, setTestEmailData] = useState({
    toEmail: '',
    subject: 'Test Email from Cursor Analytics'
  });
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [updatingUserSSO, setUpdatingUserSSO] = useState(null);

  const loadSystemHealth = async () => {
    try {
      const response = await api.get(endpoints.admin.health);
      setSystemHealth(response.data);
    } catch (error) {
      // Fallback demo data
      setSystemHealth({
        status: 'error',
        database: { status: 'disconnected', responseTime: 0 },
        storage: { used: 0, total: 0, unit: 'GB' },
        memory: { used: 0, total: 0, unit: '%' },
        uptime: 'Unknown',
        statistics: {
          totalUsers: 0,
          totalRecords: 0,
          totalUploads: 0,
          lastUpload: null
        }
      });
    }
  };

  const loadUsers = async () => {
    try {
      console.log('🔍 FRONTEND: Loading admin users...');
      const response = await api.get(endpoints.admin.users);
      console.log('✅ FRONTEND: Users loaded, count:', response.data.users?.length || 0);
      console.log('📊 FRONTEND: Users data:', response.data.users);
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('❌ FRONTEND: Load users error:', error);
      console.error('❌ FRONTEND: Error response:', error.response?.data);
      // NO FAKE DATA - show empty user list on error
      setUsers([]);
    }
  };

  const loadSendgridConfig = async () => {
    try {
      setSendgridLoading(true);
      const response = await api.get(endpoints.admin.sendgrid.config);
      setSendgridConfig(response.data.config || {});
    } catch (error) {
      console.error('Failed to load SendGrid config:', error);
      setSendgridConfig({
        configured: false,
        fromEmail: '',
        fromName: '',
        replyTo: ''
      });
    } finally {
      setSendgridLoading(false);
    }
  };

  const loadAdminData = useCallback(async () => {
    try {
      setLoading(true);
      
      switch (activeTab) {
        case 'health':
          await loadSystemHealth();
          break;
        case 'users':
          await loadUsers();
          break;
        case 'sendgrid':
          await loadSendgridConfig();
          break;
        default:
          // Default case to satisfy ESLint
          break;
      }
    } catch (error) {
      console.error('Failed to load admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    loadAdminData();
  }, [loadAdminData]);

  const createUser = async () => {
    try {
      if (!newUser.email.trim()) {
        toast.error('Email is required');
        return;
      }

      const response = await api.post(endpoints.admin.users, { email: newUser.email, role: newUser.role });
      
      // Handle the response based on whether email was sent
      if (response.data.emailSent) {
        toast.success('User invited successfully. Invitation email sent.');
      } else {
        toast.success(`User invited successfully, but invitation email could not be sent. ${response.data.emailError || 'Unknown error'}`);
      }
      
      setShowCreateUser(false);
      setNewUser({ email: '', role: 'user' });
      loadUsers();
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.error === 'User already invited') {
        toast.error('User has already been invited but has not logged in yet. Use the re-invite function.');
      } else {
        toast.error(error.response?.data?.message || 'Failed to invite user');
      }
    }
  };

  const reInviteUser = async (userId) => {
    try {
      const response = await api.post(endpoints.admin.reInviteUser(userId));
      
      // Handle the response based on whether email was sent
      if (response.data.emailSent) {
        const action = response.data.user?.action || 're-invited';
        const actionText = action === 're-invitation' ? 're-invited' : 'invitation extended';
        toast.success(`User ${actionText} successfully. ${action === 're-invitation' ? 'New invitation' : 'Updated invitation'} email sent.`);
      } else {
        const action = response.data.user?.action || 're-invited';
        const actionText = action === 're-invitation' ? 're-invited' : 'invitation extended';
        toast.success(`User ${actionText} successfully, but invitation email could not be sent. ${response.data.emailError || 'Unknown error'}`);
      }
      
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to re-invite user');
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      await api.put(endpoints.admin.userRole(userId), { role: newRole });
      toast.success('User role updated successfully');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update user role');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingUserId(userId);
      await api.delete(endpoints.admin.deleteUser(userId));
      toast.success('User deleted successfully');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete user');
    } finally {
      setDeletingUserId(null);
    }
  };

  const toggleUserSSO = async (userId, currentStatus) => {
    setUpdatingUserSSO(userId);
    try {
      await api.put(`/sso/users/${userId}/sso`, {
        ssoEnforced: !currentStatus
      });
      
      toast.success(`SSO ${!currentStatus ? 'enabled' : 'disabled'} for user`);
      await loadUsers(); // Reload to get updated data
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to update SSO settings';
      toast.error(message);
    } finally {
      setUpdatingUserSSO(null);
    }
  };

  const executeNuclearOption = async () => {
    try {
      await api.post(endpoints.admin.nuclear, {
        confirmationPassword: nuclearPassword
      });
      toast.success('Nuclear option executed successfully');
      setShowNuclearConfirm(false);
      setNuclearPassword('');
      // Reload all data
      loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Nuclear option failed');
    }
  };

  const updateSendgridConfig = async (config) => {
    try {
      setSendgridLoading(true);
      const { data } = await api.post(endpoints.admin.sendgrid.config, config);
      setSendgridConfig(data.config);
      toast.success('SendGrid configuration updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update SendGrid configuration');
    } finally {
      setSendgridLoading(false);
    }
  };

  const sendTestEmail = async () => {
    try {
      setSendingTestEmail(true);
      await api.post(endpoints.admin.sendgrid.test, testEmailData);
      toast.success('Test email sent successfully');
      setTestEmailData({ toEmail: '', subject: 'Test Email from Cursor Analytics' });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send test email');
    } finally {
      setSendingTestEmail(false);
    }
  };

  const assignTeams = async () => {
    try {
      setTeamManagementLoading(true);
      await api.post('/api/admin/assign-teams');
      toast.success('Teams assigned successfully!');
      
      // Reload team management data to show updated teams
      if (activeTab === 'teams') {
        await loadTeamManagementData();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to assign teams');
    } finally {
      setTeamManagementLoading(false);
    }
  };

  const debugTeams = async () => {
    try {
      setDebugLoading(true);
      const response = await api.get('/api/admin/debug-teams');
      setDebugOutput(response.data.debugOutput);
      toast.success('Team debug completed!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to debug teams');
    } finally {
      setDebugLoading(false);
    }
  };

  // Call backend debug endpoint for database lines (admin-only)
  const debugDatabaseLines = async () => {
    try {
      setCursorDebugLoading(true);
      setCursorDebug(null);
      const response = await api.get(endpoints.cursor.debugDatabaseLines);
      setCursorDebug(response.data);
      toast.success('Fetched database lines debug');
    } catch (error) {
      console.error('Debug database lines failed:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch database lines debug');
    } finally {
      setCursorDebugLoading(false);
    }
  };

  // Date-range database debug (admin-only)
  const debugDatabaseLinesRange = async (start, end) => {
    try {
      setCursorDebugLoading(true);
      setCursorDebug(null);
      const response = await api.get(endpoints.cursor.debugDatabaseLinesRange(start, end));
      setCursorDebug(response.data);
      toast.success('Fetched database lines for range');
    } catch (error) {
      console.error('Debug database lines (range) failed:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch database lines for range');
    } finally {
      setCursorDebugLoading(false);
    }
  };

  // Enhanced team management functions
  const loadAllTeams = async () => {
    try {
      console.log('🔍 FRONTEND: Loading all teams...');
      const response = await api.get(endpoints.admin.teams);
      console.log('✅ FRONTEND: All teams loaded, count:', response.data.teams?.length || 0);
      setAllTeams(response.data.teams || []);
    } catch (error) {
      console.error('❌ FRONTEND: Load all teams error:', error);
      setAllTeams([]);
    }
  };

  const loadAllUsersWithTeams = async () => {
    try {
      console.log('🔍 FRONTEND: Loading all CSV users with team info...');
      const response = await api.get(endpoints.admin.csvUsers);
      console.log('✅ FRONTEND: All CSV users loaded, count:', response.data.users?.length || 0);
      setAllUsers(response.data.users || []);
    } catch (error) {
      console.error('❌ FRONTEND: Load all CSV users error:', error);
      setAllUsers([]);
    }
  };

  const createNewTeam = async () => {
    if (!newTeam.name.trim()) {
      toast.error('Team name is required');
      return;
    }

    try {
      setTeamManagementLoading(true);
      await api.post(endpoints.admin.createTeam, {
        name: newTeam.name.trim(),
        description: newTeam.description.trim() || null
      });
      
      toast.success('Team created successfully!');
      setShowCreateTeam(false);
      setNewTeam({ name: '', description: '' });
      
      // Reload teams
      await loadAllTeams();
      
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create team');
    } finally {
      setTeamManagementLoading(false);
    }
  };

  const reassignUserTeam = async (userId, teamId) => {
    try {
      await api.put(endpoints.admin.userTeam(userId), { teamId });
      toast.success('User team assignment updated successfully!');
      
      // Reload users to reflect changes
      await loadAllUsersWithTeams();
      
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update user team');
    }
  };

  const updateTeamFriendlyName = async (teamId, friendlyName) => {
    try {
      await api.put(endpoints.admin.updateTeamFriendlyName(teamId), { friendlyName });
      toast.success('Team friendly name updated successfully!');
      
      // Reload teams to reflect changes
      await loadAllTeams();
      setEditingTeam(null);
      
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update team friendly name');
    }
  };

  // Upload debug functions removed - CSV upload functionality deprecated

  const loadTeamManagementData = useCallback(async () => {
    setTeamManagementLoading(true);
    try {
      await Promise.all([
        loadAllTeams(),
        loadAllUsersWithTeams()
      ]);
    } finally {
      setTeamManagementLoading(false);
    }
  }, []);

  // Maintenance mode functions
  const loadMaintenanceConfig = async () => {
    try {
      setMaintenanceLoading(true);
      const response = await api.get('/maintenance/config');
      setMaintenanceConfig(response.data);
      setTempMessage(response.data.message || '');
      
      // If table doesn't exist, don't show error toast
      if (response.data.setupRequired) {
        console.log('Maintenance table setup required');
      }
    } catch (error) {
      console.error('Error loading maintenance config:', error);
      // Handle gracefully - the backend now returns safe defaults
      // Set up default state for when table doesn't exist
      setMaintenanceConfig({
        isMaintenanceMode: false,
        message: 'The system is currently under maintenance. Please come back later. Contact itsupport@csimaverick.com if the issue persists.',
        setupRequired: true,
        enabledAt: null,
        enabledBy: null,
        enabledByUsername: null,
        disabledAt: null,
        disabledBy: null,
        disabledByUsername: null
      });
    } finally {
      setMaintenanceLoading(false);
    }
  };

  const createMaintenanceTable = async () => {
    try {
      setMaintenanceLoading(true);
      await api.post('/maintenance/setup');
      toast.success('Maintenance table created successfully');
      // Reload config after table creation
      await loadMaintenanceConfig();
    } catch (error) {
      console.error('Error creating maintenance table:', error);
      toast.error('Failed to create maintenance table');
    } finally {
      setMaintenanceLoading(false);
    }
  };

  const enableMaintenanceMode = async () => {
    try {
      setMaintenanceLoading(true);
      const response = await api.post('/maintenance/enable', {
        message: tempMessage || undefined
      });
      setMaintenanceConfig(prev => ({
        ...prev,
        isMaintenanceMode: true,
        message: response.data.maintenanceMessage
      }));
      toast.success('Maintenance mode enabled');
    } catch (error) {
      console.error('Error enabling maintenance mode:', error);
      toast.error('Failed to enable maintenance mode');
    } finally {
      setMaintenanceLoading(false);
    }
  };

  const disableMaintenanceMode = async () => {
    try {
      setMaintenanceLoading(true);
      await api.post('/maintenance/disable');
      setMaintenanceConfig(prev => ({
        ...prev,
        isMaintenanceMode: false
      }));
      toast.success('Maintenance mode disabled');
    } catch (error) {
      console.error('Error disabling maintenance mode:', error);
      toast.error('Failed to disable maintenance mode');
    } finally {
      setMaintenanceLoading(false);
    }
  };

  const updateMaintenanceMessage = async () => {
    try {
      setMaintenanceLoading(true);
      await api.put('/maintenance/message', { message: tempMessage });
      setMaintenanceConfig(prev => ({
        ...prev,
        message: tempMessage
      }));
      setEditingMessage(false);
      toast.success('Maintenance message updated');
    } catch (error) {
      console.error('Error updating maintenance message:', error);
      toast.error('Failed to update maintenance message');
    } finally {
      setMaintenanceLoading(false);
    }
  };

  // Load team management data when teams tab is selected
  useEffect(() => {
    if (activeTab === 'teams') {
      loadTeamManagementData();
    }
    // Load maintenance config when maintenance tab is active
    if (activeTab === 'maintenance') {
      loadMaintenanceConfig();
    }
  }, [activeTab, loadTeamManagementData]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
      case 'connected':
      case 'running':
        return <CheckCircle className="w-5 h-5 text-success-600" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-warning-600" />;
      case 'error':
      case 'disconnected':
        return <XCircle className="w-5 h-5 text-danger-600" />;
      default:
        return <Clock className="w-5 h-5 text-secondary-400" />;
    }
  };

  const tabs = [
    { id: 'health', name: 'System Health', icon: Activity },
    { id: 'users', name: 'User Management', icon: Users },
    { id: 'sso', name: 'SSO Configuration', icon: Shield },
    { id: 'teams', name: 'Team Management', icon: UserCheck },
    { id: 'maintenance', name: 'Maintenance Mode', icon: Settings },
    { id: 'sendgrid', name: 'SendGrid Email', icon: Mail },
    { id: 'nuclear', name: 'Nuclear Option', icon: AlertTriangle }
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-secondary-900">
          System Administration
        </h1>
        <p className="text-secondary-600 mt-1">
          Manage users, monitor system health, and advanced settings
        </p>
      </div>

      {/* Admin Warning */}
      <div className="card border-warning-200 bg-warning-50">
        <div className="card-body">
          <div className="flex items-start">
            <Shield className="w-5 h-5 text-warning-600 mr-3 mt-0.5" />
            <div>
              <h3 className="font-semibold text-warning-900 mb-1">
                Administrator Access
              </h3>
              <p className="text-warning-800 text-sm">
                You have administrator privileges. Please use these tools carefully 
                as they can affect all users and system data.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-secondary-200">
        <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 px-2 sm:px-1 border-b-2 font-medium text-sm flex items-center whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
              }`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="large" />
        </div>
      ) : (
        <>
          {/* System Health Tab */}
          {activeTab === 'health' && (
            <div className="space-y-6">
              {/* Cursor Analytics Debug */}
              <div className="card">
                <div className="card-header">
                  <h3 className="text-lg font-semibold text-secondary-900">Cursor Data Debug</h3>
                  <div className="flex gap-2">
                    <button
                      onClick={debugDatabaseLines}
                      disabled={cursorDebugLoading}
                      className="btn btn-outline"
                    >
                      {cursorDebugLoading ? 'Checking…' : 'Check Database Lines'}
                    </button>
                    <button
                      onClick={() => {
                        const start = prompt('Start date (YYYY-MM-DD)');
                        const end = prompt('End date (YYYY-MM-DD)');
                        if (start && end) debugDatabaseLinesRange(start, end);
                      }}
                      disabled={cursorDebugLoading}
                      className="btn btn-outline"
                    >
                      Range Check
                    </button>
                  </div>
                </div>
                {cursorDebug && (
                  <div className="card-body">
                    <div className="mb-3">
                      <div className="text-sm text-secondary-700 font-medium">Summary (last 30 days)</div>
                      <pre className="mt-2 p-3 bg-secondary-50 rounded border border-secondary-200 overflow-auto text-xs">
{JSON.stringify(cursorDebug.summary || {}, null, 2)}
                      </pre>
                    </div>
                    <div>
                      <div className="text-sm text-secondary-700 font-medium">Recent Records (last 7 days)</div>
                      <pre className="mt-2 p-3 bg-secondary-50 rounded border border-secondary-200 overflow-auto text-xs">
{JSON.stringify(cursorDebug.recentRecords || [], null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>

              {/* System Status Overview */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="stat-card">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="stat-value">
                        {(() => {
                          const secs = systemHealth?.system?.uptime?.process;
                          if (secs === undefined || secs === null) return '—';
                          const d = Math.floor(secs / 86400);
                          const h = Math.floor((secs % 86400) / 3600);
                          const m = Math.floor((secs % 3600) / 60);
                          const s = Math.floor(secs % 60);
                          return `${d}d ${h}h ${m}m ${s}s`;
                        })()}
                      </div>
                      <div className="stat-label">Uptime</div>
                    </div>
                    <Clock className="w-8 h-8 text-success-600" />
                  </div>
                </div>

                <div className="stat-card">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="stat-value">
                        {(() => {
                          const st = systemHealth?.services?.database?.storage;
                          if (!st) return '—';
                          const used = (st.rawDataSizeMB || 0) + (st.rawLogSizeMB || 0);
                          const total = st.rawSizeMB || 0;
                          // Display as "Used of Total" to avoid confusion
                          return `${used.toFixed(2)} MB of ${total.toFixed(2)} MB`;
                        })()}
                      </div>
                      <div className="stat-label">Storage Used</div>
                    </div>
                    <HardDrive className="w-8 h-8 text-warning-600" />
                  </div>
                </div>

                <div className="stat-card">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="stat-value">
                        {(() => {
                          const mem = systemHealth?.system?.memory;
                          if (!mem) return '—';
                          // Prefer precise MB if provided; fall back to old fields
                          const usedMB = (mem.usedMB ?? ((mem.used || 0) * 1024));
                          const totalMB = (mem.totalMB ?? ((mem.total || 0) * 1024));
                          const pct = totalMB > 0 ? Math.round((usedMB / totalMB) * 100) : (mem.percentage || 0);
                          return `${usedMB.toFixed(2)} MB / ${totalMB.toFixed(2)} MB (${pct}%)`;
                        })()}
                      </div>
                      <div className="stat-label">Memory Usage</div>
                    </div>
                    <Zap className="w-8 h-8 text-purple-600" />
                  </div>
                </div>
              </div>

              {/* Database Storage Status */}
              <div className="card mb-6">
                <div className="card-header">
                  <h3 className="text-lg font-semibold text-secondary-900">
                    Database Storage
                  </h3>
                </div>
                <div className="card-body">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <div className="text-sm text-blue-600 mb-1">Total Size</div>
                      <div className="text-2xl font-bold text-blue-900">
                        {systemHealth?.services?.database?.storage?.totalSize || '0.00 GB'}
                      </div>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                      <div className="text-sm text-green-600 mb-1">Data Size</div>
                      <div className="text-2xl font-bold text-green-900">
                        {systemHealth?.services?.database?.storage?.dataSize || '0.00 GB'}
                      </div>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <div className="text-sm text-purple-600 mb-1">Log Size</div>
                      <div className="text-2xl font-bold text-purple-900">
                        {systemHealth?.services?.database?.storage?.logSize || '0.00 GB'}
                      </div>
                    </div>
                  </div>

                  <div className="mt-6">
                    <h4 className="text-sm font-semibold text-secondary-900 mb-3">Table Storage Details</h4>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-secondary-200">
                        <thead>
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-secondary-500 uppercase">Table</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-secondary-500 uppercase">Rows</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-secondary-500 uppercase">Total Space</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-secondary-500 uppercase">Used Space</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-secondary-200">
                          {systemHealth?.services?.database?.tables?.map((table, index) => (
                            <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-secondary-50'}>
                              <td className="px-3 py-2 text-sm font-medium text-secondary-900">{table.TableName}</td>
                              <td className="px-3 py-2 text-sm text-secondary-600 text-right">{table.RowCount.toLocaleString()}</td>
                              <td className="px-3 py-2 text-sm text-secondary-600 text-right">{table.TotalSpaceMB.toFixed(2)} MB</td>
                              <td className="px-3 py-2 text-sm text-secondary-600 text-right">{table.UsedSpaceMB.toFixed(2)} MB</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              {/* Detailed Health Status */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="card">
                  <div className="card-header">
                    <h3 className="text-lg font-semibold text-secondary-900">
                      Service Status
                    </h3>
                  </div>
                  <div className="card-body">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between py-2">
                        <div className="flex items-center">
                          {getStatusIcon(systemHealth?.database?.status)}
                          <span className="ml-3 font-medium">Database</span>
                        </div>
                        <span className="text-sm text-secondary-600">
                          {systemHealth?.database?.responseTime}ms
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="card-header">
                    <h3 className="text-lg font-semibold text-secondary-900">
                      System Statistics
                    </h3>
                  </div>
                  <div className="card-body">
                    <div className="space-y-4">
                      <div className="flex justify-between">
                        <span className="text-secondary-600">Total Users:</span>
                        <span className="font-medium">{systemHealth?.statistics?.totalUsers}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-secondary-600">Total Records:</span>
                        <span className="font-medium">{systemHealth?.statistics?.totalRecords?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-secondary-600">Total Teams:</span>
                        <span className="font-medium">{systemHealth?.statistics?.totalTeams}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-secondary-600">Last Activity:</span>
                        <span className="font-medium">{formatDate(systemHealth?.statistics?.lastActivity?.RecordDate)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* User Management Tab */}
          {activeTab === 'users' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-secondary-900">
                  User Management
                </h2>
                <button
                  onClick={() => setShowCreateUser(true)}
                  className="btn-primary"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create User
                </button>
              </div>

              <div className="card">
                <div className="card-body">
                  <div className="overflow-x-auto">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Email</th>
                          <th>Role</th>
                          <th>SSO Status</th>
                          <th>Created</th>
                          <th>Last Active</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((user_item) => (
                          <tr key={user_item.Id}>
                            <td className="font-medium">{user_item.Email}</td>
                            <td>
                              <span className={`badge ${
                                user_item.Role === 'admin' ? 'badge-danger' : 'badge-primary'
                              }`}>
                                {user_item.Role}
                              </span>
                            </td>
                            <td>
                              <div className="flex items-center space-x-2">
                                {user_item.SSOEnforced ? (
                                  <div className="flex items-center space-x-1">
                                    <ShieldCheck className="h-4 w-4 text-success-600" />
                                    <span className="text-sm text-success-700">Enforced</span>
                                  </div>
                                ) : (
                                  <div className="flex items-center space-x-1">
                                    <Shield className="h-4 w-4 text-secondary-400" />
                                    <span className="text-sm text-secondary-500">Not enforced</span>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="text-sm text-secondary-600">
                              {formatDate(user_item.CreatedAt)}
                            </td>
                            <td className="text-sm text-secondary-600">
                              {(user_item.LastActive || user_item.LastLogin) ? formatDate(user_item.LastActive || user_item.LastLogin) : 'Never'}
                            </td>
                            <td>
                              {user_item.IsInvited && !user_item.IsActive ? (
                                <span className="badge badge-warning">
                                  Invited (Pending)
                                </span>
                              ) : (
                                <span className={`badge ${
                                  user_item.IsActive ? 'badge-success' : 'badge-secondary'
                                }`}>
                                  {user_item.IsActive ? 'Active' : 'Inactive'}
                                </span>
                              )}
                            </td>
                            <td>
                              <div className="flex items-center gap-2">
                                <select
                                  value={user_item.Role}
                                  onChange={(e) => updateUserRole(user_item.Id, e.target.value)}
                                  className="input input-sm"
                                  disabled={user_item.Id === user?.id} // Can't change own role
                                >
                                  <option value="user">User</option>
                                  <option value="admin">Admin</option>
                                </select>
                                
                                {/* SSO toggle button */}
                                <button
                                  onClick={() => toggleUserSSO(user_item.Id, user_item.SSOEnforced)}
                                  disabled={updatingUserSSO === user_item.Id}
                                  className={`btn btn-sm ${
                                    user_item.SSOEnforced
                                      ? 'btn-warning'
                                      : 'btn-success'
                                  }`}
                                  title={user_item.SSOEnforced ? 'Disable SSO' : 'Enable SSO'}
                                >
                                  {updatingUserSSO === user_item.Id ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                  ) : user_item.SSOEnforced ? (
                                    <Shield className="w-4 h-4" />
                                  ) : (
                                    <ShieldCheck className="w-4 h-4" />
                                  )}
                                </button>

                                {/* Re-invite button for invited users who haven't logged in */}
                                {user_item.IsInvited && !user_item.IsActive && (
                                  <button
                                    onClick={() => reInviteUser(user_item.Id)}
                                    className="btn-warning btn-sm"
                                    title="Re-invite user"
                                  >
                                    <Mail className="w-4 h-4" />
                                  </button>
                                )}
                                
                                <button
                                  onClick={() => deleteUser(user_item.Id)}
                                  className="btn-danger btn-sm"
                                  disabled={user_item.Id === user?.id || deletingUserId === user_item.Id} // Can't delete self or during deletion
                                >
                                  {deletingUserId === user_item.Id ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                  ) : (
                                    <Trash2 className="w-4 h-4" />
                                  )}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* SSO Configuration Tab */}
          {activeTab === 'sso' && (
            <div className="space-y-6">
              <SSOConfiguration />
            </div>
          )}

          {/* Team Management Tab */}
          {activeTab === 'teams' && (
            <div className="space-y-6">
              {/* Header Actions */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h2 className="text-xl font-semibold text-secondary-900">
                  Team Management
                </h2>
                <div className="flex flex-wrap gap-4">
                  <button
                    onClick={assignTeams}
                    disabled={teamManagementLoading}
                    className="btn btn-secondary flex items-center"
                  >
                    {teamManagementLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Auto-Assigning...
                      </>
                    ) : (
                      <>
                        <UserCheck className="w-4 h-4 mr-2" />
                        Auto-Assign Teams
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setShowCreateTeam(true)}
                    className="btn btn-primary flex items-center"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create New Team
                  </button>
                  <button
                    onClick={debugTeams}
                    disabled={debugLoading}
                    className="btn btn-outline flex items-center"
                  >
                    {debugLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 mr-2"></div>
                        Debugging...
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="w-4 h-4 mr-2" />
                        Debug Teams
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Debug Output Display */}
              {debugOutput && (
                <div className="card">
                  <div className="card-header">
                    <h3 className="font-semibold text-secondary-900">Team Debug Output</h3>
                    <button
                      onClick={() => setDebugOutput(null)}
                      className="text-secondary-400 hover:text-secondary-600"
                    >
                      <XCircle className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="bg-secondary-50 p-4 rounded-lg font-mono text-sm overflow-auto max-h-96">
                    {debugOutput.map((line, index) => (
                      <div key={index} className="mb-1">
                        {line}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Teams Management Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Teams List */}
                <div className="card">
                  <div className="card-header">
                    <h3 className="font-semibold text-secondary-900">All Teams</h3>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-sm text-secondary-600">
                        {allTeams.length} team{allTeams.length !== 1 ? 's' : ''} total
                      </p>
                      <input
                        type="text"
                        placeholder="Search teams..."
                        className="input input-sm"
                        onChange={(e) => setTeamSearch(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="card-body">
                    {teamManagementLoading ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
                        <p className="text-secondary-600 mt-2">Loading teams...</p>
                      </div>
                    ) : allTeams.length > 0 ? (
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {allTeams
                          .filter(team => 
                            team.Name.toLowerCase().includes(teamSearch.toLowerCase()) ||
                            (team.FriendlyName && team.FriendlyName.toLowerCase().includes(teamSearch.toLowerCase()))
                          )
                          .map((team) => (
                          <div key={team.Id} className="bg-secondary-50 rounded-lg p-4 border border-secondary-200">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-semibold text-secondary-900">
                                    {team.FriendlyName || team.Name}
                                  </h4>
                                  {team.FriendlyName && team.FriendlyName !== team.Name && (
                                    <span className="text-xs text-secondary-500 bg-secondary-200 px-2 py-1 rounded">
                                      ({team.Name})
                                    </span>
                                  )}
                                </div>
                                {editingTeam?.Id === team.Id ? (
                                  <div className="mt-2 space-y-2">
                                    <input
                                      type="text"
                                      value={editingTeam.FriendlyName || ''}
                                      onChange={(e) => setEditingTeam({...editingTeam, FriendlyName: e.target.value})}
                                      className="w-full px-2 py-1 text-sm border border-secondary-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                      placeholder="Enter friendly name"
                                      autoFocus
                                    />
                                    <div className="flex gap-2">
                                      <button
                                        onClick={() => updateTeamFriendlyName(team.Id, editingTeam.FriendlyName)}
                                        className="px-3 py-1 text-xs bg-primary-600 text-white rounded hover:bg-primary-700"
                                      >
                                        Save
                                      </button>
                                      <button
                                        onClick={() => setEditingTeam(null)}
                                        className="px-3 py-1 text-xs bg-secondary-600 text-white rounded hover:bg-secondary-700"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => setEditingTeam({ Id: team.Id, FriendlyName: team.FriendlyName || team.Name })}
                                    className="text-xs text-primary-600 hover:text-primary-800 mt-1"
                                  >
                                    Edit Friendly Name
                                  </button>
                                )}
                                <p className="text-xs text-secondary-500 mt-2">
                                  {team.userCount} user{team.userCount !== 1 ? 's' : ''}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <UserCheck className="w-12 h-12 text-secondary-400 mx-auto mb-4" />
                        <h4 className="font-semibold text-secondary-900 mb-2">No Teams Yet</h4>
                        <p className="text-secondary-600">
                          Create your first team or use auto-assignment to get started.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* CSV User Team Assignments */}
                <div className="card">
                  <div className="card-header">
                    <h3 className="font-semibold text-secondary-900">Developer Team Assignments</h3>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-sm text-secondary-600">
                        {allUsers.length} developer{allUsers.length !== 1 ? 's' : ''} total
                      </p>
                      <input
                        type="text"
                        placeholder="Search developers..."
                        className="input input-sm"
                        onChange={(e) => setUserSearch(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="card-body">
                    {teamManagementLoading ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
                        <p className="text-secondary-600 mt-2">Loading users...</p>
                      </div>
                    ) : allUsers.length > 0 ? (
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {allUsers
                          .filter(user => 
                            user.Email.toLowerCase().includes(userSearch.toLowerCase()) ||
                            user.UserId.toLowerCase().includes(userSearch.toLowerCase())
                          )
                          .map((user) => (
                          <div key={user.Id} className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg border border-secondary-200">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-secondary-900 truncate">
                                {user.Email}
                              </p>
                              <div className="text-xs text-secondary-500 space-y-1">
                                <p>User ID: {user.UserId}</p>
                                <p>Status: {user.IsActive ? 'Active' : 'Inactive'}</p>
                                {user.ManualTeamAssignment && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                    Manual Assignment
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="ml-4 flex-shrink-0">
                              <select
                                value={user.TeamId || ''}
                                onChange={(e) => reassignUserTeam(user.Id, e.target.value || null)}
                                className="text-sm border border-secondary-300 rounded px-2 py-1 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              >
                                <option value="">No Team</option>
                                {allTeams.map((team) => (
                                  <option key={team.Id} value={team.Id}>
                                    {team.FriendlyName || team.Name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Users className="w-12 h-12 text-secondary-400 mx-auto mb-4" />
                        <h4 className="font-semibold text-secondary-900 mb-2">No CSV Users Found</h4>
                        <p className="text-secondary-600">
                          CSV users will appear here once they are uploaded from CSV files.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Instructions */}
              <div className="card border-blue-200 bg-blue-50">
                <div className="card-body">
                  <div className="flex items-start">
                    <UserCheck className="w-6 h-6 text-blue-600 mr-3 mt-1" />
                    <div>
                      <h3 className="font-semibold text-blue-900">
                        Team Management Guide
                      </h3>
                      <div className="text-blue-800 text-sm mt-1 space-y-2">
                        <p>
                          <strong>CSV Users:</strong> These are users from uploaded CSV files whose Cursor usage data is being analyzed. 
                          This is separate from application login users.
                        </p>
                        <p>
                          <strong>Auto-Assignment:</strong> Automatically assigns CSV users to teams based on email domains 
                          (e.g., john@company.com → "company" team). Only affects users without manual assignments.
                        </p>
                        <p>
                          <strong>Manual Assignment:</strong> Use the dropdowns to manually assign CSV users to teams. 
                          Manual assignments override auto-assignment and are permanent for future data imports.
                        </p>
                        <p>
                          <strong>Create Teams:</strong> Create custom teams that don't match email domains 
                          for special organizational needs.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Create Team Modal */}
              {showCreateTeam && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-lg p-6 w-full max-w-md">
                    <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                      Create New Team
                    </h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">
                          Team Name *
                        </label>
                        <input
                          type="text"
                          value={newTeam.name}
                          onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
                          className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          placeholder="Enter team name"
                          autoFocus
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">
                          Description (Optional)
                        </label>
                        <textarea
                          value={newTeam.description}
                          onChange={(e) => setNewTeam({ ...newTeam, description: e.target.value })}
                          className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          placeholder="Enter team description"
                          rows="3"
                        />
                      </div>
                    </div>

                    <div className="flex justify-end gap-3 mt-6">
                      <button
                        onClick={() => {
                          setShowCreateTeam(false);
                          setNewTeam({ name: '', description: '' });
                        }}
                        className="btn btn-secondary"
                        disabled={teamManagementLoading}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={createNewTeam}
                        disabled={teamManagementLoading || !newTeam.name.trim()}
                        className="btn btn-primary"
                      >
                        {teamManagementLoading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Creating...
                          </>
                        ) : (
                          'Create Team'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Upload Debug Tab removed - CSV upload functionality deprecated */}

          {/* SendGrid Email Tab */}
          {activeTab === 'sendgrid' && (
            <div className="space-y-6">
              {/* SendGrid Configuration */}
              <div className="card">
                <div className="card-header">
                  <div className="flex items-center">
                    <Mail className="w-6 h-6 text-primary-600 mr-3" />
                    <h2 className="text-xl font-semibold text-secondary-900">
                      SendGrid Email Configuration
                    </h2>
                  </div>
                </div>
                <div className="card-body">
                  {sendgridLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <LoadingSpinner />
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Configuration Status */}
                      <div className="flex items-center justify-between p-4 bg-secondary-50 rounded-lg">
                        <div className="flex items-center">
                          <div className={`w-3 h-3 rounded-full mr-3 ${sendgridConfig.configured ? 'bg-success-500' : 'bg-warning-500'}`}></div>
                          <span className="font-medium text-secondary-900">
                            {sendgridConfig.configured ? 'SendGrid is configured' : 'SendGrid is not configured'}
                          </span>
                        </div>
                        {sendgridConfig.configured && (
                          <CheckCircle className="w-5 h-5 text-success-600" />
                        )}
                      </div>

                      {/* Configuration Form */}
                      <form onSubmit={(e) => {
                        e.preventDefault();
                        const formData = new FormData(e.target);
                        updateSendgridConfig({
                          apiKey: formData.get('apiKey'),
                          fromEmail: formData.get('fromEmail'),
                          fromName: formData.get('fromName'),
                          replyTo: formData.get('replyTo')
                        });
                      }}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="form-label">
                              SendGrid API Key <span className="text-danger-500">*</span>
                            </label>
                            <input
                              type="password"
                              name="apiKey"
                              defaultValue={sendgridConfig.apiKey || ''}
                              className="input w-full"
                              placeholder="SG.your_api_key_here"
                              required
                            />
                            <p className="text-sm text-secondary-500 mt-1">
                              Get your API key from <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">SendGrid Dashboard</a>
                            </p>
                          </div>

                          <div>
                            <label className="form-label">
                              From Email <span className="text-danger-500">*</span>
                            </label>
                            <input
                              type="email"
                              name="fromEmail"
                              defaultValue={sendgridConfig.fromEmail || ''}
                              className="input w-full"
                              placeholder="noreply@yourdomain.com"
                              required
                            />
                            <p className="text-sm text-secondary-500 mt-1">
                              Must be a verified sender in SendGrid
                            </p>
                          </div>

                          <div>
                            <label className="form-label">From Name</label>
                            <input
                              type="text"
                              name="fromName"
                              defaultValue={sendgridConfig.fromName || ''}
                              className="input w-full"
                              placeholder="Your Company Name"
                            />
                            <p className="text-sm text-secondary-500 mt-1">
                              Display name for outgoing emails
                            </p>
                          </div>

                          <div>
                            <label className="form-label">Reply To Email</label>
                            <input
                              type="email"
                              name="replyTo"
                              defaultValue={sendgridConfig.replyTo || ''}
                              className="input w-full"
                              placeholder="support@yourdomain.com"
                            />
                            <p className="text-sm text-secondary-500 mt-1">
                              Email address for replies (optional)
                            </p>
                          </div>
                        </div>

                        <div className="flex justify-end mt-6">
                          <button
                            type="submit"
                            disabled={sendgridLoading}
                            className="btn-primary"
                          >
                            {sendgridLoading ? (
                              <>
                                <LoadingSpinner size="small" />
                                Updating...
                              </>
                            ) : (
                              'Update Configuration'
                            )}
                          </button>
                        </div>
                      </form>
                    </div>
                  )}
                </div>
              </div>

              {/* Test Email Section */}
              {sendgridConfig.configured && (
                <div className="card">
                  <div className="card-header">
                    <h3 className="text-lg font-semibold text-secondary-900">
                      Send Test Email
                    </h3>
                  </div>
                  <div className="card-body">
                    <div className="space-y-4">
                      <p className="text-secondary-600">
                        Send a test email to verify your SendGrid configuration is working correctly.
                      </p>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="form-label">
                            To Email <span className="text-danger-500">*</span>
                          </label>
                          <input
                            type="email"
                            value={testEmailData.toEmail}
                            onChange={(e) => setTestEmailData({ ...testEmailData, toEmail: e.target.value })}
                            className="input w-full"
                            placeholder="test@example.com"
                          />
                        </div>

                        <div>
                          <label className="form-label">Subject</label>
                          <input
                            type="text"
                            value={testEmailData.subject}
                            onChange={(e) => setTestEmailData({ ...testEmailData, subject: e.target.value })}
                            className="input w-full"
                            placeholder="Test Email from Cursor Analytics"
                          />
                        </div>
                      </div>

                      <div className="flex justify-end">
                        <button
                          onClick={sendTestEmail}
                          disabled={!testEmailData.toEmail || sendingTestEmail}
                          className="btn-secondary"
                        >
                          {sendingTestEmail ? (
                            <>
                              <LoadingSpinner size="small" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <Mail className="w-4 h-4 mr-2" />
                              Send Test Email
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Documentation */}
              <div className="card">
                <div className="card-header">
                  <h3 className="text-lg font-semibold text-secondary-900">
                    SendGrid Setup Guide
                  </h3>
                </div>
                <div className="card-body">
                  <div className="prose prose-sm max-w-none">
                    <ol className="list-decimal list-inside space-y-2 text-secondary-700">
                      <li>
                        <strong>Create a SendGrid Account:</strong> Sign up at{' '}
                        <a href="https://sendgrid.com" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                          sendgrid.com
                        </a>
                      </li>
                      <li>
                        <strong>Verify Your Sender:</strong> Go to Settings → Sender Authentication and verify your domain or at least one sender email
                      </li>
                      <li>
                        <strong>Create an API Key:</strong> Go to Settings → API Keys and create a new API key with "Mail Send" permissions
                      </li>
                      <li>
                        <strong>Configure Above:</strong> Enter your API key and verified sender email in the configuration form above
                      </li>
                      <li>
                        <strong>Test:</strong> Use the test email feature to verify everything is working correctly
                      </li>
                    </ol>
                    
                    <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                      <h4 className="font-semibold text-blue-900 mb-2">Important Notes:</h4>
                      <ul className="text-sm text-blue-800 space-y-1">
                        <li>• The "From Email" must be verified in your SendGrid account</li>
                        <li>• API keys are stored securely and not displayed after saving</li>
                        <li>• Test emails are sent immediately when you click "Send Test Email"</li>
                        <li>• For production use, consider using domain authentication for better deliverability</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Maintenance Mode Tab */}
          {activeTab === 'maintenance' && (
            <div className="space-y-6">
              <div className="card">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-semibold text-secondary-900">Maintenance Mode</h2>
                    <p className="text-secondary-600 mt-1">
                      Control site-wide maintenance mode for regular users
                    </p>
                  </div>
                  <div className="flex items-center">
                    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                      maintenanceConfig.isMaintenanceMode 
                        ? 'bg-red-100 text-red-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {maintenanceConfig.isMaintenanceMode ? 'ACTIVE' : 'DISABLED'}
                    </span>
                  </div>
                </div>

                {maintenanceLoading && <LoadingSpinner />}

                {!maintenanceLoading && (
                  <div className="space-y-6">
                    {/* Setup Required Notice */}
                    {maintenanceConfig.setupRequired && (
                      <div className="p-4 rounded-lg border border-yellow-200 bg-yellow-50">
                        <div className="flex items-center space-x-3">
                          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                          <div className="flex-1">
                            <h3 className="font-medium text-secondary-900">
                              Setup Required
                            </h3>
                            <p className="text-sm text-secondary-600 mt-1">
                              The maintenance configuration table needs to be created in the database.
                            </p>
                            <button
                              onClick={createMaintenanceTable}
                              disabled={maintenanceLoading}
                              className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
                            >
                              Create Maintenance Table
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Current Status */}
                    {!maintenanceConfig.setupRequired && (
                      <div className={`p-4 rounded-lg border ${
                        maintenanceConfig.isMaintenanceMode 
                          ? 'border-red-200 bg-red-50' 
                          : 'border-green-200 bg-green-50'
                      }`}>
                      <div className="flex items-center space-x-3">
                        <div className={`w-3 h-3 rounded-full ${
                          maintenanceConfig.isMaintenanceMode ? 'bg-red-500' : 'bg-green-500'
                        }`}></div>
                        <div>
                          <h3 className="font-medium text-secondary-900">
                            {maintenanceConfig.isMaintenanceMode ? 'Maintenance Mode Active' : 'System Operational'}
                          </h3>
                          <p className="text-sm text-secondary-600">
                            {maintenanceConfig.isMaintenanceMode 
                              ? 'Regular users will see the maintenance page' 
                              : 'All users have normal access to the dashboard'
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                    )}

                    {/* Maintenance Message */}
                    {!maintenanceConfig.setupRequired && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <label className="block text-sm font-medium text-secondary-700">
                          Maintenance Message
                        </label>
                        <button
                          onClick={() => {
                            setEditingMessage(!editingMessage);
                            if (!editingMessage) setTempMessage(maintenanceConfig.message || '');
                          }}
                          className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                        >
                          {editingMessage ? 'Cancel' : 'Edit'}
                        </button>
                      </div>
                      
                      {editingMessage ? (
                        <div className="space-y-3">
                          <textarea
                            value={tempMessage}
                            onChange={(e) => setTempMessage(e.target.value)}
                            rows={4}
                            className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Enter maintenance message..."
                          />
                          <div className="flex space-x-2">
                            <button
                              onClick={updateMaintenanceMessage}
                              disabled={maintenanceLoading}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                            >
                              Save Message
                            </button>
                            <button
                              onClick={() => setEditingMessage(false)}
                              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                          <p className="text-sm text-secondary-700">
                            {maintenanceConfig.message || 'No custom message set'}
                          </p>
                        </div>
                      )}
                    </div>
                    )}

                    {/* Action Buttons */}
                    {!maintenanceConfig.setupRequired && (
                    <div className="flex space-x-4">
                      {!maintenanceConfig.isMaintenanceMode ? (
                        <button
                          onClick={enableMaintenanceMode}
                          disabled={maintenanceLoading}
                          className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium"
                        >
                          Enable Maintenance Mode
                        </button>
                      ) : (
                        <button
                          onClick={disableMaintenanceMode}
                          disabled={maintenanceLoading}
                          className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
                        >
                          Disable Maintenance Mode
                        </button>
                      )}
                    </div>
                    )}

                    {/* Status History */}
                    {!maintenanceConfig.setupRequired && 
                    (maintenanceConfig.enabledAt || maintenanceConfig.disabledAt) && (
                      <div className="border-t pt-6">
                        <h4 className="text-sm font-medium text-secondary-700 mb-3">Recent Activity</h4>
                        <div className="space-y-2 text-sm text-secondary-600">
                          {maintenanceConfig.enabledAt && (
                            <div className="flex justify-between">
                              <span>Last enabled:</span>
                              <span>
                                {formatDate(maintenanceConfig.enabledAt)} by {maintenanceConfig.enabledByUsername || 'Unknown'}
                              </span>
                            </div>
                          )}
                          {maintenanceConfig.disabledAt && (
                            <div className="flex justify-between">
                              <span>Last disabled:</span>
                              <span>
                                {formatDate(maintenanceConfig.disabledAt)} by {maintenanceConfig.disabledByUsername || 'Unknown'}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Nuclear Option Tab */}
          {activeTab === 'nuclear' && (
            <div className="space-y-6">
              <div className="card border-danger-200 bg-danger-50">
                <div className="card-header border-danger-200">
                  <div className="flex items-center">
                    <AlertTriangle className="w-6 h-6 text-danger-600 mr-3" />
                    <h2 className="text-xl font-semibold text-danger-900">
                      Nuclear Option - System Reset
                    </h2>
                  </div>
                </div>
                <div className="card-body">
                  <div className="space-y-4">
                    <p className="text-danger-800 font-medium">
                      ⚠️ WARNING: This will completely wipe all CSV-imported data!
                    </p>
                    
                    <div className="bg-white rounded-lg p-4 border border-danger-200">
                      <h4 className="font-semibold text-danger-900 mb-2">
                        This action will permanently delete:
                      </h4>
                      <ul className="text-sm text-danger-700 space-y-1">
                        <li>• All CSV imported users and teams</li>
                        <li>• All uploaded CSV files and data</li>
                        <li>• All analytics and dashboard data</li>
                        <li>• All upload history and logs</li>
                      </ul>
                    </div>

                    <div className="bg-white rounded-lg p-4 border border-danger-200">
                      <h4 className="font-semibold text-danger-900 mb-2">
                        After reset, the system will:
                      </h4>
                      <ul className="text-sm text-danger-700 space-y-1">
                        <li>• Reset database tables (Users, Teams, DailyRecords)</li>
                        <li>• Clear all CSV upload storage</li>
                        <li>• Restart job queues</li>
                        <li>• Maintain all admin and viewer accounts</li>
                      </ul>
                    </div>

                    <button
                      onClick={() => setShowNuclearConfirm(true)}
                      className="btn-danger"
                    >
                      <AlertTriangle className="w-4 h-4 mr-2" />
                      Initialize Nuclear Option
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create User Modal */}
      {showCreateUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-lg font-semibold text-secondary-900">
                Invite New User
              </h3>
              <button
                onClick={() => setShowCreateUser(false)}
                className="text-secondary-400 hover:text-secondary-600"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Invitation System:</strong> Users will receive an email with a temporary 12-digit password. 
                  They must log in within 72 hours to activate their account.
                </p>
              </div>
              
              <div>
                <label className="form-label">Email Address</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="input w-full"
                  placeholder="user@company.com"
                />
              </div>
              
              <div>
                <label className="form-label">Role</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  className="input w-full"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 p-6 border-t bg-secondary-50">
              <button
                onClick={() => setShowCreateUser(false)}
                className="btn-outline"
              >
                Cancel
              </button>
              <button
                onClick={createUser}
                className="btn-primary"
              >
                Send Invitation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Nuclear Confirmation Modal */}
      {showNuclearConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full border-2 border-danger-200">
            <div className="flex items-center justify-between p-6 border-b border-danger-200 bg-danger-50">
              <h3 className="text-lg font-semibold text-danger-900">
                Confirm Nuclear Option
              </h3>
              <button
                onClick={() => setShowNuclearConfirm(false)}
                className="text-danger-400 hover:text-danger-600"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <p className="text-danger-800 font-medium">
                Enter your admin password to confirm this destructive action:
              </p>
              
              <div>
                <label className="form-label">Admin Password</label>
                <input
                  type="password"
                  value={nuclearPassword}
                  onChange={(e) => setNuclearPassword(e.target.value)}
                  className="input w-full"
                  placeholder="Enter your password"
                />
              </div>
              
              <p className="text-xs text-danger-600">
                This action cannot be undone. All data will be permanently lost.
              </p>
            </div>
            
            <div className="flex justify-end gap-3 p-6 border-t bg-danger-50">
              <button
                onClick={() => setShowNuclearConfirm(false)}
                className="btn-outline"
              >
                Cancel
              </button>
              <button
                onClick={executeNuclearOption}
                className="btn-danger"
                disabled={!nuclearPassword}
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Execute Nuclear Option
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin; 